import { Injectable, inject } from '@angular/core';
import { DateTimeHelperTZ } from '../../../util/date-time-helper-tz';
import { TimeBlockRenderService } from '../rendering/time-block-render.service';
import { SubSink } from 'subsink';
import { Observable } from 'rxjs';
import { CalendarService } from '../../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../../time-mapping/time-coordinate-mapping.service';
import { CalendarEvents } from '../../../calendar-types';
import { filter, map } from 'rxjs/operators';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { CalendarServiceHelper } from '../../../services/calendar-service-helper';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeBlockDurationCalculationService } from '../calculation/duration/time-block-duration-calculation.service';
import { TimeBlockContentType, TimeBlockType } from '../types/time-block-types';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { TimeBlockMonthVariableIntervalType } from '../../../../../core/models/timeblock/time-block-view-type.model';

@Injectable()
export class TimeBlockService {
  private readonly calendarService = inject(CalendarService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly timeBlockRenderService = inject(TimeBlockRenderService);
  private readonly timeCoordinateMapperService = inject(TimeCoordinateMappingService);

  private readonly subs = new SubSink();

  constructor() {
    this.initEvents();
  }

  public static skipSecondsForIncomingTimeBlock(value: any, obj: any): Date {
    if (obj.fix_interval === null || obj.fix_interval === undefined) {
      return new Date(value);
    }

    // Skip seconds client time block to make time block calculation easier. Also remove time zone
    const date = DateTimeHelper.parseISO(DateTimeHelperTZ.removeTimezone(value));
    return DateTimeHelper.setSecondsAndMillisToZero(date);
  }

  public getMinimumBlockHeight(): Observable<number> {
    return this.timeCoordinateMapperService.pixelsPerMinuteCalculated$.pipe(
      filter((ppm) => !!ppm),
      map((ppm) => {
        const minimumTimeBlockDuration =
          this.calendarService.model.calendarProperties.minimumTimeBlockDuration;
        if (ppm && minimumTimeBlockDuration) {
          return ppm * minimumTimeBlockDuration;
        }
        return null;
      }),
    );
  }

  public isTimeBlockOutsideOfView(
    timeBlockComponentItem: ITimeBlockComponentItem,
    offsetRange = false,
  ): boolean {
    const timeBlockModel = timeBlockComponentItem.timeBlockModel;
    let calendarStart = this.calendarService.model.calendarProperties.visibleStartDate;
    let calendarEnd = this.calendarService.model.calendarProperties.visibleEndDate;

    if (offsetRange) {
      calendarStart = this.calendarService.model.calendarProperties.offsetStartDate;
      calendarEnd = this.calendarService.model.calendarProperties.offsetEndDate;
    }

    return (
      (DateTimeHelper.isBefore(timeBlockModel.start, calendarStart) &&
        DateTimeHelper.isBefore(timeBlockModel.end, calendarStart)) ||
      (DateTimeHelper.isAfter(timeBlockModel.start, calendarEnd) &&
        DateTimeHelper.isAfter(timeBlockModel.end, calendarEnd))
    );
  }

  public getTimeBlockVisiblePartCount(timeBlockComponentItem: ITimeBlockComponentItem): number {
    const visibleCalendarStart = this.calendarService.model.calendarProperties.visibleStartDate;
    const visibleCalendarEnd = this.calendarService.model.calendarProperties.visibleEndDate;

    const timeBlockModel = timeBlockComponentItem.timeBlockModel;
    const visibleTimeBlockStart = DateTimeHelper.isBefore(
      timeBlockModel.start,
      visibleCalendarStart,
    )
      ? visibleCalendarStart
      : timeBlockModel.start;
    const visibleTimeBlockEnd = DateTimeHelper.isAfter(timeBlockModel.end, visibleCalendarEnd)
      ? visibleCalendarEnd
      : timeBlockModel.end;

    return DateTimeHelper.differenceInDays(visibleTimeBlockEnd, visibleTimeBlockStart);
  }

  /**
   * A default time block is a time block with project of null (could be a tracked time block as well).
   * @param timeBlock
   */
  public isDefaultTimeBlock(timeBlock: ITimeBlockComponentItem): boolean {
    return (
      timeBlock.timeBlockContentType === TimeBlockContentType.Project &&
      !(timeBlock.timeBlockModel as TimeBlockProjectModel).project
    );
  }

  /**
   * For month view inner day time blocks we don't want to drag or resize a newly created non-existing one.
   */
  public draggingProhibited(selectedTimeBlock: ITimeBlockComponentItem): boolean {
    const tbModel = selectedTimeBlock.timeBlockModel;

    return (
      tbModel.timeBlockViewType instanceof TimeBlockMonthVariableIntervalType &&
      tbModel.type === TimeBlockType.NonExistingBlock
    );
  }

  private initEvents(): void {
    const targetEvents = [CalendarEvents.AddedTimeBlock, CalendarEvents.ReplacedTimeBlock];
    const callback = (_: any, _1: CalendarEvents[], timeBlock: ITimeBlockComponentItem): void => {
      if (
        timeBlock?.timeBlockModel.componentRef &&
        typeof timeBlock.timeBlockModel.timeBlockViewType.geometryData?.top !== 'undefined'
      ) {
        this.timeBlockRenderService.renderTimeBlock(timeBlock);
      }
    };
    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calendarService,
      callback,
      targetEvents,
    );

    this.timeBlockDurationCalculationService.initEvents();
  }
}
